import React from 'react';
import {
    Card, Switch, InputNumber, Tag, Typography, Space, Tooltip, Empty, Divider, Select,
} from 'antd';
import { Link } from 'react-router-dom';
import { ThunderboltOutlined } from '@ant-design/icons';
import { AI_FEATURE_META } from '../access-control/featureAccess';
import { languageOptions } from '../../utils/languages';

const { Text } = Typography;

const money = (v) => `$${Number(v || 0).toFixed(4)}`;
const int = (v) => Number(v || 0).toLocaleString();

// Per-feature knobs (AI_FEATURE_META[key].settingsFields) stored in
// company_ai_feature_settings.settings — patched as a whole object so the
// backend merge keeps every key.
const FeatureSettings = ({ f, fields, saving, onUpdate }) => {
    const current = (f.settings && typeof f.settings === 'object') ? f.settings : {};
    const patch = (key, value) => onUpdate(f.key, { settings: { ...current, [key]: value } });
    return (
        <>
            {fields.map((field) => {
                const value = current[field.key] === undefined || current[field.key] === null
                    ? field.default
                    : current[field.key];
                let control = null;
                if (field.type === 'switch') {
                    control = (
                        <Switch
                            size="small"
                            checked={!!value}
                            loading={saving === f.key}
                            onChange={(v) => patch(field.key, v)}
                        />
                    );
                } else if (field.type === 'language') {
                    control = (
                        <Select
                            size="small"
                            showSearch
                            optionFilterProp="label"
                            style={{ width: 150 }}
                            value={value}
                            options={languageOptions(value)}
                            loading={saving === f.key}
                            onChange={(v) => patch(field.key, v)}
                        />
                    );
                }
                return (
                    <Tooltip key={field.key} title={field.help}>
                        <Space size={6}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{field.label}</Text>
                            {control}
                        </Space>
                    </Tooltip>
                );
            })}
        </>
    );
};

// One row per AI-group feature (registry-driven — see config/features.js). AI features are
// company-wide (ADR-033): the Enabled switch IS the entitlement — it puts the feature's bundle on
// this company's license (no seat, nothing assigned to users) and every user / bot of the company
// gets it. Monthly cap limits spend without changing entitlement. A per-user AI feature (none
// today) would show a read-only "offered on ‹license›" tag here instead of the switch.
const FeatureRow = ({ f, companyId, saving, onUpdate }) => {
    const meta = AI_FEATURE_META[f.key];
    const companyScoped = f.scope === 'company';
    const status = companyScoped
        ? null
        : (f.licensed
            ? <Tag color="green">Offered on {f.licensed_via && f.licensed_via.length ? f.licensed_via.join(', ') : 'a license'}</Tag>
            : <Tag>Not on any license</Tag>);
    let stateNote;
    if (!f.licensed) {
        stateNote = companyScoped
            ? 'Off for this company. Flip Enabled to turn it on for everyone here — no user assignment needed.'
            : 'Add its bundle to a license above, then assign seats via each user’s Access action.';
    } else if (f.cap_reached) stateNote = 'Monthly cap reached — off until the 1st (raise or clear the cap to resume).';
    else stateNote = companyScoped ? 'Enabled company-wide — every user and bot under this company.' : 'Active for users holding the bundle.';
    const outcomes = f.month && f.month.by_outcome ? f.month.by_outcome : {};
    const intentSplit = f.key === 'ai-intent-fallback' && (
        <Text type="secondary" style={{ fontSize: 11 }}>
            dictionary {int((outcomes.normalized || 0) + (outcomes.dictionary || 0) + (outcomes['previous-step'] || 0))}
            {' · '}model {int((outcomes['model-token'] || 0) + (outcomes['model-window'] || 0) + (outcomes['model-clarification'] || 0))}
            {' · '}unresolved {int(outcomes.unresolved || 0)}
            {outcomes.capped ? ` · capped ${int(outcomes.capped)}` : ''}
        </Text>
    );
    // ADR-035: translated vs already-in-language vs served from cache (free) vs unresolved
    const translationSplit = f.key === 'ai-translation' && (
        <Text type="secondary" style={{ fontSize: 11 }}>
            translated {int(outcomes.translated || 0)}
            {' · '}same language {int(outcomes['same-language'] || 0)}
            {' · '}cached {int(outcomes['cache-hit'] || 0)}
            {' · '}unresolved {int(outcomes.unresolved || 0)}
        </Text>
    );
    const settingsFields = meta && Array.isArray(meta.settingsFields) ? meta.settingsFields : null;

    return (
        <div style={{ border: '1px solid var(--gh-gray-200, #e6e6e6)', borderRadius: 8, padding: 14, marginBottom: 12, background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Text strong style={{ flex: 1, minWidth: 180 }}>
                    {f.label}
                    {companyScoped && <Tag style={{ marginLeft: 8, fontSize: 10 }}>company-wide</Tag>}
                </Text>
                {status}
                {companyScoped && (
                    <Tooltip title="Turns the feature on for everyone under this company. Recorded on the company’s license (visible on the Licenses card) but uses no seat and needs no per-user assignment.">
                        <Space size={6}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Enabled</Text>
                            <Switch
                                size="small"
                                checked={!!f.licensed}
                                loading={saving === f.key}
                                onChange={(v) => onUpdate(f.key, { enabled: v })}
                            />
                        </Space>
                    </Tooltip>
                )}
                <Tooltip title="Model calls allowed per calendar month. Blank = uncapped. Commits when you leave the field.">
                    <Space size={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Monthly cap</Text>
                        <InputNumber
                            size="small"
                            min={0}
                            style={{ width: 96 }}
                            placeholder="∞"
                            defaultValue={f.monthly_call_cap === null || f.monthly_call_cap === undefined ? undefined : f.monthly_call_cap}
                            // commit on blur with a dirty check — onChange fires per keystroke
                            onBlur={(e) => {
                                const raw = e.target.value;
                                const next = raw === '' || raw === undefined ? null : Number(raw);
                                const cur = f.monthly_call_cap === undefined ? null : f.monthly_call_cap;
                                if (next !== cur && !(Number.isNaN(next))) onUpdate(f.key, { monthly_call_cap: next });
                            }}
                        />
                    </Space>
                </Tooltip>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{stateNote}</Text>
                <Text style={{ fontSize: 12 }}>
                    This month: <b>{int(f.month.calls)}</b> model call{f.month.calls === 1 ? '' : 's'}
                    {f.monthly_call_cap !== null && f.monthly_call_cap !== undefined ? ` of ${int(f.monthly_call_cap)}` : ''}
                    {' · '}<b>{money(f.month.cost_usd)}</b>
                </Text>
                {intentSplit}
                {translationSplit}
                {meta && meta.manage && (
                    <Link to={meta.manage.to(companyId)} style={{ fontSize: 12 }}>{meta.manage.label} →</Link>
                )}
            </div>
            {settingsFields && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
                    <FeatureSettings f={f} fields={settingsFields} saving={saving} onUpdate={onUpdate} />
                </div>
            )}
        </div>
    );
};

const AiFeaturesPanel = ({ companyId, ai }) => {
    const other = ai.otherUsage;
    const otherFeatures = other && other.by_feature ? Object.entries(other.by_feature) : [];
    return (
        <Card
            className="gh-dash-card"
            title={<span><ThunderboltOutlined style={{ marginRight: 8 }} />AI</span>}
            extra={(
                <Text type="secondary" style={{ fontSize: 12 }}>
                    This month, all AI: <b>{money(ai.totals.month_cost_usd)}</b>
                    {' · '}<Link to={`/ai-usage?company=${companyId}`}>Full history →</Link>
                </Text>
            )}
            loading={ai.loading && !ai.features.length}
        >
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>
                Every AI feature this company can hold. AI features are <b>company-wide</b>: flip <b>Enabled</b> and
                every user and bot under the company has it — nothing is assigned per user and no seat is used.
                <b> Monthly cap</b> limits model calls per calendar month without changing who is entitled. A feature
                is live only when enabled and under its cap.
            </Text>
            {ai.features.length === 0
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No AI features registered" />
                : ai.features.map((f) => (
                    <FeatureRow key={f.key} f={f} companyId={companyId} saving={ai.saving} onUpdate={ai.update} />
                ))}
            {other && (other.month.calls > 0 || other.month.no_model_events > 0) && (
                <>
                    <Divider style={{ margin: '10px 0' }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Other AI usage (built-in, not licensable): <b>{int(other.month.calls)}</b> calls · <b>{money(other.month.cost_usd)}</b>
                        {otherFeatures.length ? ` — ${otherFeatures.map(([k, v]) => `${k} ${int(v.calls)}`).join(', ')}` : ''}
                    </Text>
                </>
            )}
        </Card>
    );
};

export default AiFeaturesPanel;
